import { CourseProgressData, CourseProgressFlow, FlowAssignment, Flow } from "types";
import { extractImageKey, resolveSignedImageUrl } from "utils/signedImageUrl";
import { forgetPersistentImage, getPersistentImageUrl } from "utils/persistentImageCache";

const preloadedImages = new Set<string>();
const preloadingImages = new Set<string>();

const normalizeImageRef = (value?: string | null): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const imageReferenceKey = (reference: string): string => {
  const key = extractImageKey(reference);
  try {
    // Storage keys are already literal; only URL paths need decoding.
    return reference.startsWith('http') ? decodeURIComponent(key) : key;
  } catch {
    return key;
  }
};

const resolvePreloadSrc = async (rawUrl: string): Promise<string | null> => {
  try {
    return await resolveSignedImageUrl(rawUrl);
  } catch (error) {
    const looksLikeDirectPath =
      rawUrl.startsWith("http") ||
      rawUrl.startsWith("/") ||
      rawUrl.startsWith("./") ||
      rawUrl.startsWith("../");

    return looksLikeDirectPath ? rawUrl : null;
  }
};

const preloadImage = async (rawUrl: string): Promise<void> => {
  const url = rawUrl?.trim();
  if (!url) {
    return;
  }

  // New content/signatures must consult the revision cache again. Key-only
  // deduplication could suppress preloading a replacement for the whole session.
  const dedupeKey = url;
  if (preloadedImages.has(dedupeKey) || preloadingImages.has(dedupeKey)) {
    return;
  }

  preloadingImages.add(dedupeKey);

  try {
    const src = await resolvePreloadSrc(url);
    if (!src) {
      return;
    }

    const image = new Image();
    const retainedSrc = getPersistentImageUrl(src);
    image.onerror = () => {
      image.onerror = null;
      if (retainedSrc !== src) {
        forgetPersistentImage(src);
        getPersistentImageUrl(src);
        image.src = src;
      }
    };
    image.src = retainedSrc;
    preloadedImages.add(dedupeKey);
  } finally {
    preloadingImages.delete(dedupeKey);
  }
};

const resolveFlowImageUrl = (flow?: CourseProgressFlow | null): string | null => {
  if (!flow) {
    return null;
  }

  const media = flow.media ?? flow.flow_details?.media ?? null;
  const signedImage =
    normalizeImageRef(flow.image_signed_url) ??
    normalizeImageRef(flow.flow_details?.image_signed_url);
  // Summary signatures describe the nested flow image. Keep a distinct
  // placement override, but use fresh signatures for the usual flow media.
  const placementKey = normalizeImageRef(flow.media?.storage_key) ?? normalizeImageRef(flow.media?.url);
  const placementOverridesSignature = flow.media?.media_type === 'image' && placementKey && signedImage &&
    imageReferenceKey(placementKey) !== imageReferenceKey(signedImage);
  if (signedImage && !placementOverridesSignature) {
    return signedImage;
  }
  if (media?.media_type === 'image') {
    const mediaUrl = normalizeImageRef(media.url);
    if (mediaUrl) {
      return mediaUrl;
    }
    const mediaStorageKey = normalizeImageRef(media.storage_key);
    if (mediaStorageKey) {
      return mediaStorageKey;
    }
  }

  if (signedImage) {
    return signedImage;
  }

  const primaryImage = normalizeImageRef(flow.flow_details?.image_url);
  if (primaryImage) {
    return primaryImage;
  }

  const legacyImage = normalizeImageRef((flow as unknown as { image_url?: string }).image_url);
  return legacyImage ?? null;
};

const resolveStandaloneFlowImageUrl = (
  assignment?: FlowAssignment | null
): string | null => {
  if (!assignment) {
    return null;
  }

  const flow: Flow | undefined = assignment.flow;
  const signedImage =
    normalizeImageRef(assignment.image_signed_url) ??
    normalizeImageRef(flow?.image_signed_url);
  if (signedImage) {
    return signedImage;
  }
  const media = flow?.media ?? null;
  if (media?.media_type === "image") {
    const mediaUrl = normalizeImageRef(media.url);
    if (mediaUrl) {
      return mediaUrl;
    }
    const mediaStorageKey = normalizeImageRef(media.storage_key);
    if (mediaStorageKey) {
      return mediaStorageKey;
    }
  }

  const primaryImage = normalizeImageRef(flow?.image_url);
  if (primaryImage) {
    return primaryImage;
  }

  return null;
};

export const preloadCourseImages = (courseProgress?: CourseProgressData | null) => {
  if (!courseProgress?.flows?.length) {
    return;
  }

  const flows = courseProgress.flows;

  const primaryFlow = flows.find((flow) => !flow.completed_at) ?? flows[0];

  const urls = new Set<string>();
  const addUrl = (flow?: CourseProgressFlow | null) => {
    const url = resolveFlowImageUrl(flow);
    if (url) {
      urls.add(url);
    }
  };

  addUrl(primaryFlow);
  flows.slice(0, 3).forEach(addUrl);

  urls.forEach((url) => {
    void preloadImage(url);
  });
};

export const getFlowImageUrl = resolveFlowImageUrl;
export const getFlowAssignmentImageUrl = resolveStandaloneFlowImageUrl;
